import { useEffect, useRef, useState } from 'react';

import { ROCK_COLS, ROCK_ROWS, fogAlpha, rockShading, rockSprite } from './RockField';
import { ROCK_URL } from '@/game/data/rockAtlas';
import {
  FLOOR_SINK, KNOCKOUT_SECONDS, anchorOffset, debrisAt, embedding, sizeOf,
  type DebrisSpec,
} from '@/game/engine/field';
import { project, type Viewport } from '@/game/engine/projection';
import { sound } from '@/game/audio/sound';
import { useRun, type KnockOut, type SettledRock } from '@/store/run';

/**
 * The rock that just came out of the wall, on its way to the floor.
 *
 * Every other rock on this screen is stone the player may hit. This one is not: it has
 * already been broken, its loot is already decided, and it is on screen for less than a
 * second purely so the wall behaves like a wall. Before this layer existed the last swing
 * made a rock *vanish* — five taps of work and the stone blinked out, which is the single
 * clearest tell that the cave is a picture rather than a place.
 *
 * **It is not interactive and it is not a spawn.** It reads from `run.debris`, which holds
 * only what an animation needs (`KnockOut`), and it takes no pointer events. Nothing here
 * can be tapped, so nothing here can pay — the rock's payout was settled by the server
 * before the first frame of the fall.
 *
 * ## Why the arc is a function of time, not an integrator
 *
 * `debrisAt(t, spec)` is a closed-form solve in corridor space (see `field.ts`), so the
 * rock follows the same path on a slow phone as on a fast one and a dropped frame skips
 * ahead instead of changing the trajectory. This layer only asks "what time is it" and
 * draws the answer; it owns no physics of its own.
 *
 * The result comes back in corridor coordinates (`u`, `v`) and goes through `project()` —
 * the same projection the wall rocks use — so the falling rock stays in the corridor's
 * perspective instead of sliding down the flat screen. A rock that broke six metres away
 * falls a short distance in screen pixels because it is six metres away.
 */
export function RockDebris({ depth, view, lightPx }: {
  depth: number;
  view: Viewport;
  lightPx: number;
}) {
  const debris = useRun((s) => s.debris);
  const settled = useRun((s) => s.settled);
  const settleDebris = useRun((s) => s.settleDebris);

  /* Which rocks have already had their cues. A rAF loop runs sixty times a second and every
     one of these must fire exactly once — a `landed` test alone would play the thud on every
     frame after the rock stops moving, which is a buzz, not an impact. */
  const announced = useRef(new Set<string>());

  /* A frame counter, not the state itself. The positions are derived from the clock on
     every paint, so there is nothing to store — keeping the arc in React state would mean
     re-deriving it twice and letting the two copies disagree. */
  const [, setFrame] = useState(0);

  /*
   * One rAF loop, alive only while something is falling.
   *
   * Keyed on `debris.length` rather than `debris` so the loop is not torn down and rebuilt
   * on every render, and stopped entirely when the array is empty — an idle cave must not
   * be running an animation frame, and this layer is mounted for the whole run.
   */
  useEffect(() => {
    if (debris.length === 0) return;

    let raf = 0;
    const frame = (now: number) => {
      /* Read the store rather than the closed-over array: the loop outlives several renders
         and the closure would go stale. */
      for (const d of useRun.getState().debris) {
        const t = (now - d.startedAt) / 1000;

        /* The sound of the rock giving way. `break` has existed in the sound engine since it
           was written and nothing ever played it — the single loudest beat in the loop, the
           moment five swings pay off, was silent. It belongs here rather than in the store
           because this layer is where the break becomes something you can see. */
        if (!announced.current.has(d.uuid)) {
          announced.current.add(d.uuid);
          sound.playSfx('break');
        }

        /* And the sound of it hitting the floor, once. */
        const thud = `${d.uuid}:landed`;
        if (!announced.current.has(thud) && debrisAt(t, specFor(d, depth, view, lightPx)).landed) {
          announced.current.add(thud);
          sound.playSfx('rubble');
        }

        if (t >= KNOCKOUT_SECONDS) {
          /* Hand the rock to the floor at the point the arc actually ended, so it does not
             move on the frame it stops being animated. */
          const end = debrisAt(KNOCKOUT_SECONDS, specFor(d, depth, view, lightPx));
          settleDebris(d.uuid, end.u, end.v, end.spin);
          announced.current.delete(d.uuid);
          announced.current.delete(thud);
        }
      }
      setFrame((f) => f + 1);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [debris.length, settleDebris, depth, view, lightPx]);

  if (debris.length === 0 && settled.length === 0) return null;

  const now = performance.now();

  return (
    <>
      {debris.map((d) => {
        const piece = draw(d, now, depth, view, lightPx);
        if (!piece) return null;
        const { spec, state, x, y, left, top, opacity, z } = piece;
        const cell = rockSprite(spec.materialKey, 4);

        return (
          <i
            key={d.uuid}
            aria-hidden
            /* Named so the fall can be sampled from outside — the arc lasts under a
               second, which is too short to catch in a screenshot by hand. */
            data-debris={d.uuid}
            className="pointer-events-none absolute block rounded-[38%]"
            style={{
              left,
              top,
              width: spec.size,
              height: spec.size,
              opacity,
              zIndex: z,
              transform: `rotate(${state.spin.toFixed(1)}deg)`,
              WebkitTransform: `rotate(${state.spin.toFixed(1)}deg)`,
              ...(cell
                ? {
                    backgroundImage: `url(${ROCK_URL})`,
                    backgroundSize: `${ROCK_COLS * 100}% ${ROCK_ROWS * 100}%`,
                    backgroundPosition: `${cell.px}% ${cell.py}%`,
                  }
                : { background: 'radial-gradient(circle at 34% 30%, #6E5240, #2C1E16 72%)' }),
              /* Lit like every other rock in the corridor, and never as "tappable" — the
                 brighter tint is how this screen says "you can hit this", and a rock that
                 is already broken must not claim it. */
              filter: rockShading({ x, y, size: spec.size, view, worked: false, tappable: false }),
            }}
          />
        );
      })}

      {/* What the run has already broken, lying where it fell.
          Before this the rock fell for 0.9s and then blinked out — which put the vanishing
          back, just delayed by a second. Walking back up the tunnel should mean walking past
          your own rubble. Not tappable, not a spawn, and carrying no reward: these rocks were
          paid out before they hit the floor. */}
      {settled.map((r) => {
        const at = drawSettled(r, depth, view, lightPx);
        if (!at) return null;
        const cell = rockSprite(r.materialKey, 4);

        return (
          <i
            key={r.uuid}
            aria-hidden
            data-settled={r.uuid}
            className="pointer-events-none absolute block rounded-[38%]"
            style={{
              left: at.left,
              top: at.top,
              width: at.size,
              height: at.size,
              opacity: at.opacity,
              zIndex: at.z,
              transform: `rotate(${at.spin}deg)`,
              WebkitTransform: `rotate(${at.spin}deg)`,
              ...(cell
                ? {
                    backgroundImage: `url(${ROCK_URL})`,
                    backgroundSize: `${ROCK_COLS * 100}% ${ROCK_ROWS * 100}%`,
                    backgroundPosition: `${cell.px}% ${cell.py}%`,
                  }
                : { background: 'radial-gradient(circle at 34% 30%, #6E5240, #2C1E16 72%)' }),
              filter: rockShading({
                x: at.x, y: at.y, size: at.size, view, worked: false, tappable: false,
              }),
            }}
          />
        );
      })}
    </>
  );
}

/**
 * The physics input for one knocked-out rock.
 *
 * Exported and shared because the arc is asked for in two places — the render, and the rAF
 * loop that decides when it has landed. Two constructions of the same spec would eventually
 * differ by a field, and the loop would then be watching a slightly different rock from the
 * one on screen.
 */
export function specFor(d: KnockOut, depth: number, view: Viewport, lightPx: number): DebrisSpec {
  const rel = d.depthM - depth;
  const p = project(d.u, d.v, rel, view, lightPx);
  return {
    uuid: d.uuid,
    u: d.u,
    v: d.v,
    d: rel,
    slot: d.slot,
    materialKey: d.materialKey,
    /* The size it had in the wall. Recomputed from the same rule rather than carried in the
       store, so a rock cannot change size at the moment it comes loose. */
    size: sizeOf(d.uuid, d.slot, p.scale),
  };
}

/**
 * Rubble that has stopped moving, projected where it lies.
 *
 * No clock and no physics: it landed, and the only thing that changes it now is the player
 * walking, which changes its depth like everything else in the corridor.
 */
export function drawSettled(r: SettledRock, depth: number, view: Viewport, lightPx: number) {
  const rel = r.depthM - depth;
  const p = project(r.u, r.v, rel, view, lightPx);
  /* Culled once the lamp no longer reaches it. The store keeps at most `SETTLED_MAX` pieces,
     but the lamp is what actually decides how much of your own mess you can see. */
  if (p.opacity <= 0.02) return null;

  const size = sizeOf(r.uuid, 'floor', p.scale);
  return {
    size,
    x: p.x,
    y: p.y,
    /* Anchored like every other piece of floor rubble: hanging from its base and sunk into
       the ground, not centred in mid-air (`layoutField` does the same for floor spawns). */
    left: p.x - size / 2,
    top: p.y - size * (1 - FLOOR_SINK),
    opacity: fogAlpha(p.opacity),
    z: p.z,
    /* The angle the tumble actually landed at (`r.spin`, set by `settleDebris` from the
       physics arc) — not a fresh hash roll. Rolling a new angle here used to make the
       rock visibly snap to a different rotation on the exact frame it stopped falling,
       which read as the settle hitching rather than completing. */
    spin: r.spin,
  };
}

/**
 * One falling rock, from a `KnockOut` and a clock reading.
 *
 * Split out so the arithmetic is testable without a DOM: everything above is JSX, and
 * everything that decides where the rock actually is happens here.
 *
 * The depth is re-derived from the player's own depth each frame (`depthM - depth`) rather
 * than frozen at the break, exactly as `layoutField` does it. In practice the player
 * cannot move during the fall — `phase` is `'loot'` — but a placement rule that is true
 * only while something else holds still is a placement rule waiting to be wrong.
 */
export function draw(d: KnockOut, now: number, depth: number, view: Viewport, lightPx: number) {
  const t = (now - d.startedAt) / 1000;
  if (t < 0) return null;

  const rel = d.depthM - depth;
  const spec = specFor(d, depth, view, lightPx);
  const state = debrisAt(t, spec);
  /* Where it IS now, not where it broke. */
  const at = project(state.u, state.v, rel, view, lightPx);

  /*
   * Anchored exactly as the wall rock was, for the whole fall.
   *
   * At t = 0 the falling rock must land on the same pixels the embedded one occupied — the
   * only thing that changes on that frame is that the buried half is no longer clipped
   * away, which is the point. Re-centring it on the corridor point instead would jump it
   * sideways by up to `EMBED_FRACTION` of its width as it came loose.
   */
  const { offX, offY } = anchorOffset(embedding(d.slot, d.u, d.uuid), spec.size, d.slot === 'floor');

  return {
    spec,
    state,
    x: at.x,
    y: at.y,
    left: at.x + offX,
    top: at.y + offY,
    opacity: fogAlpha(at.opacity) * state.alpha,
    /* Above the wall it came out of: it is nearer the camera than the face now, and a
       rock that fell behind its own hole would be a hole with nothing in front of it. */
    z: at.z + 1,
  };
}
